import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  X,
  Eye,
  Check,
  Building,
  Calendar,
} from 'lucide-react';
import { api } from '@/lib/api';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const PriceDriftAlertsSection = ({ onModalStateChange }) => {
  const { theme } = useTheme();
  const currentColors = colors[theme];
  const [driftAlerts, setDriftAlerts] = useState([]);
  const [driftSummary, setDriftSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filters, setFilters] = useState({ status: '', severity: '' });
  const [selectedAlert, setSelectedAlert] = useState(null);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.status) params.status = filters.status;
      if (filters.severity) params.severity = filters.severity;
      const response = await api.getPriceDriftAlerts(params);
      setDriftAlerts(response.data.alerts || []);
      setDriftSummary(response.data.summary || null);
    } catch (e) {
      console.error('Error loading drift alerts:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleScan = async () => {
    setScanning(true);
    try {
      await api.triggerDriftScan(7);
      await loadAlerts();
    } catch (e) {
      console.error('Error triggering scan:', e);
    } finally {
      setScanning(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await api.acknowledgeDriftAlert(alertId);
      loadAlerts();
    } catch (e) {
      console.error(e);
    }
  };

  const handleResolve = async (alertId, notes) => {
    try {
      await api.resolveDriftAlert(alertId, notes);
      loadAlerts();
      setSelectedAlert(null);
      onModalStateChange?.(false);
    } catch (e) {
      console.error(e);
    }
  };

  const sevCfg = (s) =>
    ({
      CRITICAL: { bg: 'bg-red-100', text: 'text-red-800', icon: 'text-red-600' },
      HIGH: { bg: 'bg-orange-100', text: 'text-orange-800', icon: 'text-orange-600' },
      WARNING: { bg: 'bg-amber-100', text: 'text-amber-800', icon: 'text-amber-600' },
    })[s] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: 'text-gray-600' };

  const stCfg = (s) =>
    ({
      OPEN: { bg: 'bg-red-50', text: 'text-red-700' },
      ACKNOWLEDGED: { bg: 'bg-amber-50', text: 'text-amber-700' },
      RESOLVED: { bg: 'bg-green-50', text: 'text-green-700' },
    })[s] || { bg: 'bg-gray-50', text: 'text-gray-700' };

  return (
    <motion.div className="mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="price-drift-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DollarSign size={20} style={{ color: currentColors.accentRed }} />
          <h2 className="text-lg font-bold" style={{ color: currentColors.textPrimary }}>
            Price Drift Alerts
          </h2>
          {driftSummary?.by_status?.OPEN > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
              {driftSummary.by_status.OPEN} open
            </span>
          )}
        </div>
        <button
          onClick={handleScan}
          disabled={scanning}
          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="price-drift-scan-btn"
        >
          <RefreshCw size={14} className={scanning ? 'animate-spin' : ''} />
          {scanning ? 'Scanning...' : 'Scan Now'}
        </button>
      </div>

      {/* Summary Cards */}
      {driftSummary && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Total', value: driftSummary.total, style: { background: currentColors.cardBg }, textColor: currentColors.textPrimary, filter: { status: '', severity: '' } },
            { label: 'Critical', value: driftSummary.by_severity?.CRITICAL || 0, style: { background: '#fef2f2' }, textColor: '#b91c1c', filter: { status: '', severity: 'CRITICAL' } },
            { label: 'High', value: driftSummary.by_severity?.HIGH || 0, style: { background: '#fff7ed' }, textColor: '#c2410c', filter: { status: '', severity: 'HIGH' } },
            { label: 'Warning', value: driftSummary.by_severity?.WARNING || 0, style: { background: '#fffbeb' }, textColor: '#b45309', filter: { status: '', severity: 'WARNING' } },
          ].map((card) => (
            <motion.div
              key={card.label}
              className="rounded-xl p-3 cursor-pointer"
              style={{ ...card.style, boxShadow: getNeumorphicShadow(theme) }}
              onClick={() => setFilters(card.filter)}
              whileHover={{ scale: 1.02 }}
              data-testid={`drift-summary-${card.label.toLowerCase()}`}
            >
              <p className="text-xs" style={{ color: card.textColor }}>{card.label}</p>
              <p className="text-xl font-bold" style={{ color: card.textColor }}>{card.value}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Filter Pills */}
      {(filters.status || filters.severity) && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs" style={{ color: currentColors.textSecondary }}>Filtering:</span>
          {['status', 'severity'].map(
            (k) =>
              filters[k] && (
                <span
                  key={k}
                  className="px-2 py-0.5 text-xs rounded-full flex items-center gap-1"
                  style={{ background: currentColors.background, color: currentColors.textSecondary }}
                >
                  {filters[k]}
                  <button onClick={() => setFilters((f) => ({ ...f, [k]: '' }))}>
                    <X size={12} />
                  </button>
                </span>
              )
          )}
          <button onClick={() => setFilters({ status: '', severity: '' })} className="text-xs text-indigo-600 hover:underline">
            Clear
          </button>
        </div>
      )}

      {/* Alert List */}
      <div className="rounded-xl overflow-hidden" style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}>
        {loading ? (
          <div className="flex items-center justify-center h-32" data-testid="drift-loading">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        ) : driftAlerts.length === 0 ? (
          <div className="text-center py-12" data-testid="drift-empty">
            <AlertTriangle className="mx-auto mb-3" size={36} style={{ color: currentColors.textSecondary }} />
            <p className="text-sm" style={{ color: currentColors.textSecondary }}>No price drift alerts</p>
            <p className="text-xs mt-1" style={{ color: currentColors.textSecondary }}>Run a scan to detect deviations</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: currentColors.border }}>
            {driftAlerts.map((alert) => {
              const pct = alert.deviation_pct * 100;
              const sc = sevCfg(alert.deviation_severity);
              const st = stCfg(alert.status);
              return (
                <div
                  key={alert.alert_id}
                  className={`p-3 ${alert.status === 'RESOLVED' ? 'opacity-50' : ''}`}
                  data-testid={`drift-alert-${alert.alert_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg ${sc.bg} flex items-center justify-center flex-shrink-0`}>
                        {alert.direction === 'up' ? (
                          <TrendingUp className={sc.icon} size={16} />
                        ) : (
                          <TrendingDown className={sc.icon} size={16} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="font-semibold text-sm" style={{ color: currentColors.textPrimary }}>
                            {alert.ingredient_name}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${sc.bg} ${sc.text}`}>
                            {alert.deviation_severity}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${st.bg} ${st.text}`}>{alert.status}</span>
                        </div>
                        <p className="text-xs" style={{ color: currentColors.textSecondary }}>
                          Billed ₹{alert.actual_price_per_base_uom?.toFixed(2)}/{alert.base_uom} vs ₹
                          {alert.canonical_price_per_base_uom?.toFixed(2)}/{alert.base_uom} —{' '}
                          <span className={`font-bold ${alert.direction === 'up' ? 'text-red-600' : 'text-green-600'}`}>
                            {pct > 0 ? '+' : ''}
                            {pct.toFixed(1)}%
                          </span>
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: currentColors.textSecondary }}>
                          {alert.vendor_name && (
                            <span className="flex items-center gap-1">
                              <Building size={11} />
                              {alert.vendor_name}
                            </span>
                          )}
                          {alert.bill_date && (
                            <span className="flex items-center gap-1">
                              <Calendar size={11} />
                              {new Date(alert.bill_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {alert.status !== 'RESOLVED' && (
                      <div className="flex gap-1.5 flex-shrink-0">
                        {alert.status === 'OPEN' && (
                          <button
                            onClick={() => handleAcknowledge(alert.alert_id)}
                            className="px-2 py-1 text-xs border rounded-lg hover:bg-gray-50 flex items-center gap-1"
                            style={{ borderColor: currentColors.border, color: currentColors.textSecondary }}
                            data-testid={`drift-ack-${alert.alert_id}`}
                          >
                            <Eye size={12} /> Ack
                          </button>
                        )}
                        <button
                          onClick={() => { setSelectedAlert(alert); onModalStateChange?.(true); }}
                          className="px-2 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                          data-testid={`drift-resolve-${alert.alert_id}`}
                        >
                          <Check size={12} /> Resolve
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Resolve Modal */}
      <AnimatePresence>
        {selectedAlert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => { setSelectedAlert(null); onModalStateChange?.(false); }}
            data-testid="resolve-drift-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
              data-testid="resolve-drift-modal"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-3">Resolve Alert</h3>
              <p className="text-sm text-gray-600 mb-3">
                Resolve price alert for <strong>{selectedAlert.ingredient_name}</strong>
              </p>
              <textarea
                placeholder="Notes (optional)..."
                id="drift-resolve-notes"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 h-20 text-sm"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setSelectedAlert(null); onModalStateChange?.(false); }}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  data-testid="drift-resolve-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleResolve(selectedAlert.alert_id, document.getElementById('drift-resolve-notes')?.value || '')
                  }
                  className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                  data-testid="drift-resolve-confirm-btn"
                >
                  Resolve
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PriceDriftAlertsSection;
